
import { axiosConsignadoInstance } from "../axiosInstance";

const USE_MOCK_CONSUMO_LOTE =
  import.meta.env.VITE_USE_CONSUMO_LOTE_MOCKS === "true";

export type ConsumoCotaCorbanItem = {
  id: string;
  idCorban: number;
  numeroConsultas: number;
  numeroConsultasSucesso: number;
  /** Total de CCBs pagas (coluna numero_ccbs_pagas no backend) */
  numeroCcbs: number;
  consultasMargem: number;
  cotaAtiva: boolean;
  consumoBloqueado: boolean;
  numeroRef?: number;
  dataFimConsumo?: string | null;
  createdAt: string;
  updatedAt?: string;
  nomeCorban?: string;
  nomePromotora?: string | null;
};

export const getConsumoCotasCorbanAtivas = async (
  page: number,
  pageSize: number,
  search?: string,
): Promise<{
  data: ConsumoCotaCorbanItem[];
  total: number;
  totalPages: number;
}> => {
  if (USE_MOCK_CONSUMO_LOTE) {
    const { getMockConsumoCotasCorbanAtivas } = await import(
      "@/mocks/consumoCotasCorban.mock"
    );
    return getMockConsumoCotasCorbanAtivas(page, pageSize, search);
  }
  const trimmed = search?.trim();
  const response = await axiosConsignadoInstance.get(
    "/api-consignado-trabalhador/v1/consumo-cotas-corban",
    {
      params: {
        page,
        pageSize,
        ...(trimmed ? { search: trimmed } : {}),
      },
    },
  );
  return response.data;
};

export const downloadConsumoCotasCorbanExcel = async (
  page: number,
  pageSize: number,
  search?: string,
): Promise<Blob> => {
  const trimmed = search?.trim();
  const response = await axiosConsignadoInstance.get(
    "/api-consignado-trabalhador/v1/consumo-cotas-corban/excel",
    {
      params: {
        page,
        pageSize,
        ...(trimmed ? { search: trimmed } : {}),
      },
      responseType: "blob",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  return response.data;
};

/**
 * Exporta o consumo em lote para Excel.
 * TODO: MOCKADO — o backend ainda não definiu o endpoint de Excel do consumo
 * em lote. Quando existir, trocar pela chamada real (ex.: GET
 * `/consultas-info-lote/excel` com `responseType: "blob"`), nos moldes de
 * `downloadConsumoCotasCorbanExcel`.
 */
export const downloadConsultasInfoLoteExcel = async (
  search?: string,
): Promise<Blob> => {
  const { mockDownloadConsultasInfoLoteExcel } = await import(
    "@/mocks/consultasInfoLote.mock"
  );
  return mockDownloadConsultasInfoLoteExcel(search);
};

export const getCotasInativasByCorbanId = async (
  idCorban: number,
): Promise<ConsumoCotaCorbanItem[]> => {
  if (USE_MOCK_CONSUMO_LOTE) {
    const { getMockCotasInativasByCorbanId } = await import(
      "@/mocks/consumoCotasCorban.mock"
    );
    return getMockCotasInativasByCorbanId(idCorban);
  }
  const response = await axiosConsignadoInstance.get(
    `/api-consignado-trabalhador/v1/consumo-cotas-corban/${idCorban}/inativas`,
  );
  return response.data;
};

export type GerarRelatorioConsumoCorbanInfo = {
  numeroConsultas: number;
  numeroConsultasSucesso: number;
  numeroCcbsPagas: number;
  consultasMargem: number;
};

/**
 * Gera o relatório individual de consumo de um corban e retorna o link do
 * arquivo (Excel) salvo no storage.
 * `dataInicio`/`dataFim` devem estar no formato DD/MM/YYYY (esperado pelo backend).
 */
export const gerarRelatorioConsumoCorban = async (
  idCorban: number,
  body: {
    dataInicio: string;
    dataFim?: string;
    infoConsumo: GerarRelatorioConsumoCorbanInfo;
  },
): Promise<{ fileUrl: string }> => {
  if (USE_MOCK_CONSUMO_LOTE) {
    const { mockGerarRelatorioConsumoCorban } = await import(
      "@/mocks/consumoCotasCorban.mock"
    );
    return mockGerarRelatorioConsumoCorban(idCorban, body);
  }
  const response = await axiosConsignadoInstance.post(
    `/api-consignado-trabalhador/v1/consumo-cotas-corban/${idCorban}/gerar-relatorio`,
    body,
  );
  return response.data;
};

export const bloquearConsumoCorban = async (
  idCorban: number,
): Promise<void> => {
  if (USE_MOCK_CONSUMO_LOTE) {
    const { mockBloquearConsumoCorban } = await import(
      "@/mocks/consumoCotasCorban.mock"
    );
    return mockBloquearConsumoCorban(idCorban);
  }
  await axiosConsignadoInstance.patch(
    `/api-consignado-trabalhador/v1/consumo-cotas-corban/${idCorban}/bloquear`,
  );
};

export const liberarConsumoCorban = async (
  idCorban: number,
): Promise<void> => {
  if (USE_MOCK_CONSUMO_LOTE) {
    const { mockLiberarConsumoCorban } = await import(
      "@/mocks/consumoCotasCorban.mock"
    );
    return mockLiberarConsumoCorban(idCorban);
  }
  await axiosConsignadoInstance.post(
    `/api-consignado-trabalhador/v1/consumo-cotas-corban/${idCorban}/cotas`,
  );
};

export type ConsultasInfoLoteAtivoItem = {
  id: string;
  corbanId: number;
  numeroConsulta: number;
  consumoBloqueado: boolean;
  cotaAtiva: boolean;
  dataCriacao: string;
  updatedAt?: string;
  nomeCorban?: string;
  nomePromotora?: string;
};

export type ConsultasInfoLoteInativoItem = ConsultasInfoLoteAtivoItem;

export type ConsultasInfoLotePaginatedResponse<T> = {
  data: T[];
  total: number;
  totalPages: number;
};

export const getConsultasInfoLoteAtivo = async (
  page: number,
  pageSize: number,
  search?: string,
): Promise<ConsultasInfoLotePaginatedResponse<ConsultasInfoLoteAtivoItem>> => {
  if (USE_MOCK_CONSUMO_LOTE) {
    const { getMockConsultasInfoLoteAtivo } = await import(
      "@/mocks/consultasInfoLote.mock"
    );
    return getMockConsultasInfoLoteAtivo(page, pageSize);
  }
  const trimmed = search?.trim();
  const response = await axiosConsignadoInstance.get(
    "/api-consignado-trabalhador/v1/consultas-info-lote/consumo-ativo",
    {
      params: {
        page,
        pageSize,
        ...(trimmed ? { search: trimmed } : {}),
      },
    },
  );
  return response.data;
};

export const getConsultasInfoLoteInativoByCorbanId = async (
  corbanId: number,
  page: number,
  pageSize: number,
): Promise<ConsultasInfoLotePaginatedResponse<ConsultasInfoLoteInativoItem>> => {
  if (USE_MOCK_CONSUMO_LOTE) {
    const { getMockConsultasInfoLoteInativo } = await import(
      "@/mocks/consultasInfoLote.mock"
    );
    return getMockConsultasInfoLoteInativo(corbanId, page, pageSize);
  }
  const response = await axiosConsignadoInstance.get(
    `/api-consignado-trabalhador/v1/consultas-info-lote/consumo-inativo/${corbanId}`,
    {
      params: { page, pageSize },
    },
  );
  return response.data;
};

/**
 * Gera o relatório individual de consultas em lote de um corban e retorna o
 * link do arquivo (Excel) salvo no storage.
 * `dataInicio`/`dataFim` devem estar no formato DD/MM/YYYY.
 * Diferente do consumo de cotas, este endpoint NÃO recebe `infoConsumo` e
 * retorna a URL como string pura.
 */
export const gerarRelatorioConsultasInfoLote = async (
  idCorban: number,
  body: { dataInicio: string; dataFim?: string },
): Promise<string> => {
  if (USE_MOCK_CONSUMO_LOTE) {
    const { mockGerarRelatorioConsultasInfoLote } = await import(
      "@/mocks/consultasInfoLote.mock"
    );
    return mockGerarRelatorioConsultasInfoLote(idCorban, body);
  }
  const response = await axiosConsignadoInstance.post(
    `/api-consignado-trabalhador/v1/consultas-info-lote/${idCorban}/gerar-relatorio`,
    body,
  );
  // O backend retorna a URL como string pura (mas tolera { fileUrl }).
  const data = response.data;
  return typeof data === "string" ? data : (data?.fileUrl ?? "");
};

export const bloquearConsultasInfoLote = async (
  id: string,
  bloqueado: boolean,
): Promise<void> => {
  if (USE_MOCK_CONSUMO_LOTE) {
    const { mockBloquearConsultasInfoLote } = await import(
      "@/mocks/consultasInfoLote.mock"
    );
    return mockBloquearConsultasInfoLote(id, bloqueado);
  }
  await axiosConsignadoInstance.patch(
    `/api-consignado-trabalhador/v1/consultas-info-lote/bloquear/${id}`,
    { bloqueado },
  );
};